import { useEffect, useState } from "react";
import api, { formatEUR, currentMonth, monthLabel, formatDateFR, API } from "@/lib/api";
import { TrendingUp, TrendingDown, Wallet, Sparkles, AlertTriangle, FileDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import AddTransactionDialog from "@/components/AddTransactionDialog";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    const month = currentMonth();
    const [s, t, a, c, al] = await Promise.all([
      api.get(`/analytics/summary?month=${month}`),
      api.get(`/transactions?month=${month}`),
      api.get("/accounts"),
      api.get("/categories"),
      api.get("/alerts"),
    ]);
    setSummary(s.data);
    setTxs(t.data.slice(0, 6));
    setAccounts(a.data);
    setCategories(c.data);
    setAlerts(al.data);
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/reports/pdf?month=${currentMonth()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `budgeo-${currentMonth()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport téléchargé");
    } catch {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
  const accMap = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const totalBalance = accounts.reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-stone-500 capitalize">{monthLabel(currentMonth())}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Bonjour {user?.name?.split(" ")[0] || ""}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadPdf} variant="outline" className="rounded-xl border-stone-300" data-testid="btn-download-pdf">
            <FileDown className="w-4 h-4 mr-1" /> PDF
          </Button>
          <AddTransactionDialog accounts={accounts} categories={categories} onCreated={load} />
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="alerts-list">
          {alerts.map((a) => (
            <Card key={a.id}
                  className={`p-4 rounded-2xl border-l-4 ${
                    a.type === "overrun"
                      ? "border-l-rose-500 bg-rose-50/50 border-rose-100"
                      : "border-l-amber-500 bg-amber-50/50 border-amber-100"
                  }`}
                  data-testid={`alert-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  a.type === "overrun" ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-700"
                }`}>
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{a.category_name}</div>
                  <div className="text-xs text-stone-600 mt-0.5">{a.message}</div>
                  <div className="text-xs text-stone-500 mt-1 font-mono-num">
                    {formatEUR(a.spent)} / {formatEUR(a.budget)}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <Card className="p-6 rounded-2xl border-stone-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Solde total</div>
            <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-stone-700" />
            </div>
          </div>
          <div className="font-display text-3xl font-bold font-mono-num" data-testid="kpi-balance">{formatEUR(totalBalance)}</div>
          <div className="text-xs text-stone-500 mt-1">{accounts.length} compte(s)</div>
        </Card>
        <Card className="p-6 rounded-2xl border-stone-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Revenus du mois</div>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
          <div className="font-display text-3xl font-bold text-emerald-600 font-mono-num" data-testid="kpi-income">
            {formatEUR(summary?.income || 0)}
          </div>
        </Card>
        <Card className="p-6 rounded-2xl border-stone-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-transform">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">Dépenses du mois</div>
            <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-rose-600" />
            </div>
          </div>
          <div className="font-display text-3xl font-bold text-rose-600 font-mono-num" data-testid="kpi-expense">
            {formatEUR(summary?.expense || 0)}
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="p-6 rounded-2xl border-stone-200 lg:col-span-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg">Évolution sur 6 mois</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#78716C" }} />
                <YAxis tick={{ fontSize: 12, fill: "#78716C" }} />
                <Tooltip formatter={(v) => formatEUR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Revenus" fill="#10B981" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" name="Dépenses" fill="#F43F5E" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-stone-200 lg:col-span-4">
          <h3 className="font-display font-semibold text-lg mb-4">Répartition des dépenses</h3>
          {(summary?.by_category?.length || 0) === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-stone-400 text-center">
              Ajoutez des dépenses pour voir la répartition
            </div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={summary.by_category} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {summary.by_category.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatEUR(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* AI Insight card */}
      <Card className="p-6 rounded-2xl border-amber-200 bg-amber-50/50">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-lg text-amber-950">Conseil IA</h3>
            <p className="text-amber-900/80 text-sm mt-1">Obtenez une analyse personnalisée de votre budget par notre IA.</p>
            <Link to="/conseils" className="inline-block mt-3 text-sm font-medium text-amber-900 underline underline-offset-4"
                  data-testid="link-ai-advice">
              Demander un conseil →
            </Link>
          </div>
        </div>
      </Card>

      {/* Recent transactions */}
      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg">Transactions récentes</h3>
          <Link to="/transactions" className="text-sm text-stone-500 hover:text-stone-900" data-testid="link-all-tx">
            Voir tout →
          </Link>
        </div>
        {txs.length === 0 ? (
          <div className="py-10 text-center text-stone-400 text-sm">Aucune transaction ce mois-ci</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {txs.map((t) => {
              const cat = catMap[t.category_id];
              const acc = accMap[t.account_id];
              return (
                <div key={t.id} className="flex items-center gap-4 py-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: (cat?.color || "#78716C") + "20", color: cat?.color || "#78716C" }}>
                    <span className="text-xs font-bold">{cat?.name?.slice(0, 2).toUpperCase() || "??"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{t.description || cat?.name || "Transaction"}</div>
                    <div className="text-xs text-stone-500">{formatDateFR(t.date)} · {acc?.name}</div>
                  </div>
                  <div className={`font-semibold font-mono-num text-sm ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.type === "income" ? "+" : "−"}{formatEUR(t.amount)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
